import React, { Component } from 'react';
import { MetricToImperialConverter, MassUnit } from 'fitbeat.models';
import { connect } from 'react-redux';
import moment from 'moment-timezone';
import { translate } from 'react-i18next';
import { verticalScale } from 'react-native-size-matters';
import { Field, reduxForm, SubmissionError, getFormSyncErrors } from 'redux-form';
import { View, ScrollView, StyleSheet, Keyboard, TouchableOpacity, Platform, KeyboardAvoidingView } from 'react-native';

import Accordion from 'react-native-collapsible/Accordion';
import { SettingsProfile } from './components';
import { clearMealsForTimeOfDay } from '../Nutrition/actions';
import {
  Button,
} from '../components';
import Theme from '../theme';
import { AccordingHeader, DatePicker, Dropdown, FormError, GoogleAddressSearch, TextInput } from '../ui/components';
import { showMessage } from '../ui/components/Toast';
import { UserDataConsumer } from '../utils';
import { defaultScale } from '../utils/scaling';
import {
  minLength3,
  isNumber,
  validValue,
  isRequired,
  isNameValid,
  isNameValidNotRequired,
  isEmailValid,
  isPhoneNumberValid,
  isNumberOrEmpty,
} from '../utils/validator';
import {
  isEmpty,
  isValidDate,
  isDateOlderThan16,
  focusOnInput,
  getRegionsForCountry,
  normalizeDateDDMMYYYY,
  sanitizeHeartRateMonitorId,
} from '../utils';

import { ResponsiveLayout } from '../utils/ResponsiveLayout';
import CountryRegionData from '../utils/CountryRegionData';
import { GenderDropdown, HeightField, WeightField } from '../ui';
import { BaseUserProfile } from 'fitbeat.models';
import { hasAccessToAStudioGymSelector } from '../auth/auth-selectors';
const isMobile = window.innerWidth <= 550;

class ProfileTab extends Component {
  constructor(props) {
    super(props);
    let { t, user: { userProfile : { height,useImperialUnit,weight }}, hasAccessToAStudioGym} = this.props;
    const userIsActive = BaseUserProfile.hasActivePlan(props.user.userProfile);
    const shouldValidate = userIsActive;

    const { country, state } = this.props.user.userProfile;

    const countries = CountryRegionData.map(([countryName]) => {
      return { countryName };
    });

    const regions = getRegionsForCountry(country);
    this.state = {
      forms: [{
        title: t('details'),
        content: () => this.detailsFields(),
      },{
        title: t('metrics'),
        content: () => this.measurementsFields(),
        isLast: !hasAccessToAStudioGym,
      },
      ...(hasAccessToAStudioGym ? [{
        title: t('address'),
        content: () => this.addressFields(),
        isLast: hasAccessToAStudioGym,
      }] : [])],
      regions,
      countries,
      country,
      state,
      shouldValidate,
      isBeltNumberCaptured: !!(this.props.user.userProfile.heartRateMonitorIdentifier),
      isLoading: false,
      initiallyActiveSection: Platform.OS === 'web' ? 0 : -1,
      isOpeningAccordion: false,
    };

    const defaultProfile = {
      weight: '',
      height: '',
      age: '',
      addressLine1: '',
      addressLine2: '',
      postCode: '',
    };
    let heightImperial = {inches: '', feet: ''};
    if (useImperialUnit) {
      if (height) {
        heightImperial = MetricToImperialConverter.convertCentimetersToFeetAndInches(height);
      }
    }
    const heightAndWeight = {
      height: height ? height.toString() : '',
      weight: weight ? this.convertWeight(useImperialUnit, weight) : '',
      heightImperial:JSON.stringify(heightImperial)
    }
    this.initializeForm({ ...defaultProfile, ...this.props.user.userProfile, ...heightAndWeight});
  }

  convertWeight = (useImperialUnit, weight) => {
    return useImperialUnit
      ? MetricToImperialConverter.convertAmount(
          MassUnit.Killogram,
          MassUnit.Pound,
          weight
        ).toString()
      : parseFloat(weight).toFixed(2);
  };

  setIsLoading = (value) => {
    this.setState({ isLoading: value });
  }

  initializeForm = (userProfile) => {
    const { initialize } = this.props;
    if (userProfile.dateOfBirth) {
      userProfile.dateOfBirth = moment(userProfile.dateOfBirth, 'YYYY-MM-DD').format('DD-MM-YYYY');
    }
    initialize(userProfile);
  }

  onSubmit = async (values) => {
    const { t, reset, user: { userProfileService,userProfile } } = this.props;

    try {
      this.setIsLoading(true);
      Keyboard.dismiss();
      this.setActiveIndex(-1);

      const fullname = `${values.firstname} ${values.lastname}`;

      // username defaults to email now.
      // It is the field by which we search, and we later want to allow a user to change
      // it appart from the email so that they can have additional privacy
      const username = values.email;

      const userProfileDetails = {
        ...values,
        fullname,
        username,
      };

      if (userProfileDetails.dateOfBirth) {
        userProfileDetails.dateOfBirth = moment(userProfileDetails.dateOfBirth, 'DD-MM-YYYY').format('YYYY-MM-DD');
      }
      userProfileDetails.heartRateMonitorIdentifier = sanitizeHeartRateMonitorId(userProfileDetails.heartRateMonitorIdentifier);
      let userProfileDetailsWithFloat = Object.assign({}, userProfileDetails);
      if (userProfileDetailsWithFloat.height && userProfileDetailsWithFloat.height.length > 0) {
        userProfileDetailsWithFloat.height = parseFloat(userProfileDetailsWithFloat.height);
      }
      if (userProfileDetailsWithFloat.weight && userProfileDetailsWithFloat.weight.length > 0) {
        userProfileDetailsWithFloat.weight = parseFloat(userProfileDetailsWithFloat.weight);
      }
      if (userProfile.useImperialUnit) {
        const parsedHeightObj = JSON.parse(userProfileDetails.heightImperial);
        userProfileDetailsWithFloat.height =MetricToImperialConverter.convertInchesAndFeetToCentimeters(
          parsedHeightObj)
        userProfileDetailsWithFloat.weight = MetricToImperialConverter.convertAmount(MassUnit.Pound,
              MassUnit.Killogram, userProfileDetailsWithFloat.weight);
      }
      this.props.clearMealsForTimeOfDay();
      await userProfileService.updateUserProfile(userProfileDetailsWithFloat);
      showMessage(t('profileUpdatedSuccess'));
      this.initializeForm(userProfileDetails);
    } catch (error) {
      console.log(error);
      throw new SubmissionError({ _error: error });
    } finally {
      this.setIsLoading(false);
      reset('ProfileTabForm');
    }
  }

  detailsFields = () => {
    const { t } = this.props;
    const { shouldValidate } = this.state;

    return (
      <View>
        <ResponsiveLayout
          col={2}
          thresholdSize="medium">
          <Field
            trim
            forwardRef
            name="firstname"
            component={TextInput}
            autoCapitalize="none"
            keyboardType="default"
            returnKeyType="next"
            placeholder={t('formFields:firstname')}
            validate={isNameValid}
            ref={(ref) => { this.firstnameInput = ref; }}
            onSubmitEditing={() => focusOnInput(this.lastnameInput)}
          />

          <Field
            trim
            forwardRef
            name="lastname"
            component={TextInput}
            autoCapitalize="none"
            keyboardType="default"
            returnKeyType="next"
            placeholder={t('formFields:lastname')}
            validate={isNameValid}
            onSubmitEditing={() => focusOnInput(this.nicknameInput)}
            ref={(ref) => { this.lastnameInput = ref; }}
          />
          <Field
            trim
            forwardRef
            name="nickname"
            component={TextInput}
            autoCapitalize="none"
            keyboardType="default"
            returnKeyType="next"
            placeholder={t('formFields:nickname')}
            validate={[isNameValidNotRequired, minLength3]}
            onSubmitEditing={() => focusOnInput(this.mobileNumberInput)}
            ref={(ref) => { this.nicknameInput = ref; }}
          />

          <Field
            forwardRef
            editable={false}
            name="email"
            component={TextInput}
            autoCapitalize="none"
            keyboardType="email-address"
            returnKeyType="next"
            placeholder={t('formFields:emailUsername')}
            onSubmitEditing={() => focusOnInput(this.mobileNumberInput)}
            validate={[isRequired, isEmailValid]}
            ref={(ref) => { this.emailInput = ref; }}
          />

          <Field
            forwardRef
            name="mobileNumber"
            component={TextInput}
            autoCapitalize="none"
            placeholder={t('formFields:mobileNumber')}
            keyboardType="default"
            returnKeyType="next"
            isRequired={false}
            validate={[isPhoneNumberValid, validValue]}
            ref={(ref) => { this.mobileNumberInput = ref; }}
            onSubmitEditing={() => focusOnInput(this.dateOfBirthInput)}
          />

          <GenderDropdown />

          <Field
            forwardRef
            name="dateOfBirth"
            component={DatePicker}
            normalize={normalizeDateDDMMYYYY}
            placeholder={Platform.OS === 'web' ? t('formFields:dateOfBirthWeb') : t('formFields:dateOfBirth')}
            validate={[isValidDate, isDateOlderThan16, shouldValidate ? isRequired : validValue]}
            ref={(ref) => { this.dateOfBirthInput = ref; }}
          />

        </ResponsiveLayout>
      </View>
    );
  }

  measurementsFields = () => {
    const { t,user:{userProfile:{useImperialUnit}}} = this.props;
    const { shouldValidate } = this.state;
    const isWeightRequired = shouldValidate || this.state.isBeltNumberCaptured;

    return (
      <View>
        <ResponsiveLayout
          col={2}
          thresholdSize="medium">
          <WeightField
            showLabel={true}
            isInlineValidation={!useImperialUnit}
            useImperialUnit={useImperialUnit}
            isImperialContainerWidth={useImperialUnit}
           />
          <HeightField showLabel={true} useImperialUnit={useImperialUnit}/>
        </ResponsiveLayout>
      </View>
    );
  }

  onCountrySelect = async (countryName) => {
    const regions = getRegionsForCountry(countryName);
    await this.setState({
      country: countryName,
      regions,
    });

    this.props.change('country', countryName);
    this.props.change('state', null);
    this.props.untouch('state');
  }

  onRegionSelect = (regionName) => {
    this.setState({ state: regionName });
    this.props.change('state', regionName);
  }

  onAddressPress = async (details) => {
    this.props.change('addressLine1', details.addressLine1);
    this.props.change('addressLine2', details.addressLine2);
    this.props.change('suburb', details.administrative_area_level_2);
    this.props.change('postCode', details.postal_code);

    await this.onCountrySelect(details.country);
    this.onRegionSelect(details.administrative_area_level_1);
  }

  getDropdownRowWidth = () => {
    if (Platform.OS === 'web') {
      return isMobile ? '92%' : '27%';
    } else {
      return '92%';
    }
  }

  addressFields = () => {
    const { t } = this.props;
    const { countries, regions, shouldValidate } = this.state;

    return (
      <View>
        <GoogleAddressSearch onAddressPress={this.onAddressPress} />
        <ResponsiveLayout
          col={2}
          thresholdSize="medium">
          <Field
            forwardRef
            name="addressLine1"
            component={TextInput}
            autoCapitalize="none"
            placeholder={t('formFields:addressLine1')}
            keyboardType="default"
            returnKeyType="next"
            onSubmitEditing={() => focusOnInput(this.addressLine2Input)}
            validate={validValue}
            ref={(ref) => { this.addressLine1Input = ref; }}
          />

          <Field
            forwardRef
            isRequired={false}
            name="addressLine2"
            component={TextInput}
            autoCapitalize="none"
            placeholder={t('formFields:addressLine2')}
            keyboardType="default"
            returnKeyType="done"
            onSubmitEditing={() => focusOnInput(this.suburbInput)}
            ref={(ref) => { this.addressLine2Input = ref; }}
          />

          <Field
            forwardRef
            name="suburb"
            component={TextInput}
            autoCapitalize="none"
            placeholder={t('formFields:suburb')}
            keyboardType="default"
            returnKeyType="done"
            onSubmitEditing={() => focusOnInput(this.postalCodeInput)}
            validate={validValue}
            ref={(ref) => { this.suburbInput = ref; }}
          />

          <Field
            forwardRef
            name="postCode"
            component={TextInput}
            autoCapitalize="none"
            placeholder={t('formFields:postCode')}
            keyboardType="default"
            returnKeyType="done"
            onSubmitEditing={() => focusOnInput(this.stateInput)}
            validate={[isNumberOrEmpty]}
            ref={(ref) => { this.postalCodeInput = ref; }}
          />

          <Field
            forwardRef
            name="country"
            placeholder={t('formFields:country')}
            valueKey="countryName"
            displayKey="countryName"
            defaultIndex={-1}
            options={countries}
            component={Dropdown}
            isSearchable={true}
            rowWidth={this.getDropdownRowWidth()}
            onSelect={this.onCountrySelect}
            defaultValue={t('formFields:country')}
            validate={validValue}
          />

          <Field
            forwardRef
            name="state"
            placeholder={t('formFields:state')}
            valueKey="regionName"
            displayKey="regionName"
            isSearchable={true}
            defaultIndex={-1}
            options={regions}
            component={Dropdown}
            rowWidth={this.getDropdownRowWidth()}
            defaultValue={t('formFields:state')}
            validate={validValue}
          />

        </ResponsiveLayout>
      </View>
    );
  }

  setActiveIndex = (value) => {
    this.setState({ activeSection: value });
  }

  renderHeader = (section, index, isActive) => (
    <AccordingHeader
      index={index}
      section={section}
      isActive={isActive}
      setActiveIndex={this.setActiveIndex}
    />
  );

  renderContent = section => (
    <View style={styles.content}>
      {section.content()}
    </View>
  );

  OpenRelevantAccordion = (fieldsWithErrors) => {
    const { t } = this.props;
    const accordionFields = [
      [t('formFields:firstname'), t('formFields:lastname'), t('formFields:emailUsername'), t('formFields:nickname'), t('formFields:mobileNumber'), t('formFields:gender'), t('formFields:dateOfBirthWeb')],
      [t('formFields:weight'), t('formFields:height')],
      [t('formFields:addressLine1'), t('formFields:suburb'), t('formFields:postCode'), t('formFields:country'), t('formFields:state')]
    ];

    for (let singleErrorFieldIndex = 0; singleErrorFieldIndex < fieldsWithErrors.length; singleErrorFieldIndex++) {
      for (let singleAccordionIndex = 0; singleAccordionIndex < accordionFields.length; singleAccordionIndex++) {
        const errorFieldFound = accordionFields[singleAccordionIndex].find(singleField => singleField === t(`formFields:${fieldsWithErrors[singleErrorFieldIndex]}`));
        if (errorFieldFound) {
          this.setState({ activeSection: singleAccordionIndex });
          return singleAccordionIndex;
        }
        continue;
      }
    }
  }

  componentDidUpdate(nextProps) {
    const {
      t,
      submitFailed,
      formSyncErrors = {},
    } = this.props;


    const { isOpeningAccordion } = this.state;
    if ((!isEmpty(formSyncErrors) && submitFailed) && !isOpeningAccordion) {
      this.setState({ isOpeningAccordion: true });
      this.OpenRelevantAccordion(Object.keys(formSyncErrors));
    }
  }

  saveNewDetails = (event) => {
    this.setState({ isOpeningAccordion: false });
    this.props.handleSubmit(this.onSubmit)(event);
  }

  render() {
    const {
      t,
      user,
      error,
      submitFailed,
      handleSubmit,
      formSyncErrors = {},
    } = this.props;
    const {
      isLoading,
      initiallyActiveSection,
    } = this.state;

    let errorDisplay = error;
    if (!isEmpty(formSyncErrors) && submitFailed) {
      errorDisplay = new Error(t('formFields:missingFieldsError'));
    }

    return (
      <View style={styles.container} className="full-height">
        <ScrollView keyboardShouldPersistTaps="handled">
          <SettingsProfile t={t} userProfile={user.userProfile} navigation={this.props.navigation} />
          <Accordion
            sections={this.state.forms}
            activeSection={this.state.activeSection}
            renderHeader={this.renderHeader}
            renderContent={this.renderContent}
            touchableComponent={TouchableOpacity}
            initiallyActiveSection={initiallyActiveSection}
          />

        </ScrollView>
        <KeyboardAvoidingView
          behavior='padding'
          keyboardVerticalOffset={verticalScale(80)}
          enabled={Platform.OS === 'ios'}
        >
          <FormError error={errorDisplay} />
          <Button
            fontColor={Theme.WHITE}
            isLoading={isLoading}
            containerStyle={styles.saveButtonContainer}
            onPress={this.saveNewDetails}
            title={t('saveSettings')}
          />
        </KeyboardAvoidingView>
      </View>
    );
  }
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: Theme.WHITE,
    padding: defaultScale(15),
    ...Platform.select({
      web: {
        paddingHorizontal: 30,
        paddingVertical: 0,
      },
    }),
  },
  content: {
    paddingBottom: defaultScale(15),
  },
  labelText: {
    color: Theme.GREY,
    marginVertical: defaultScale(3)
  },
  saveButtonContainer: {
    marginVertical: defaultScale(15),
    ...Platform.select({
      web: {
        marginRight: 10,
        paddingHorizontal: 10,
        alignSelf: 'flex-end',
      },
    }),
  },
});

const mapStateToProps = (state) => {
  return {
    formSyncErrors: getFormSyncErrors('ProfileTabForm')(state),
    hasAccessToAStudioGym : hasAccessToAStudioGymSelector(state)
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    clearMealsForTimeOfDay: () => dispatch(clearMealsForTimeOfDay()),
  };
}

ProfileTab = UserDataConsumer(ProfileTab);
ProfileTab = connect(mapStateToProps, mapDispatchToProps)(ProfileTab);
ProfileTab = translate(['settings', 'formFields'], { wait: true })(ProfileTab);
ProfileTab = reduxForm({ form: 'ProfileTabForm' })(ProfileTab);
export default ProfileTab;
